import { createContext, useContext, ReactNode, useEffect, useRef } from 'react';
import { useSystemGetPublicConfig } from '@/core/api/generated/system/system';
import i18n from '@/core/i18n/i18n';

export interface PublicConfig {
  googleMapsApiKey?: string;
  consentBannerEnabled?: boolean;
  termsAcceptanceMode?: string;
  termsVersion?: string;
  termsAndConditionsContent?: string;
  marketingConsentTermsContent?: string;
  contactEmail?: string;
  mercadopagoPublicKey?: string;
  vectorStoreEnabled?: boolean;
  onesignalAppId?: string;
  newRelic?: {
    accountId?: string;
    trustKey?: string;
    agentId?: string;
    licenseKey?: string;
    appId?: string;
  };
  // Locale detected by backend based on IP geolocation and Accept-Language
  detectedLocale?: string;
}

interface PublicConfigContextValue {
  config: PublicConfig | null;
  isLoading: boolean;
  error: Error | null;
}

const PublicConfigContext = createContext<PublicConfigContextValue | undefined>(undefined);

export function PublicConfigProvider({ children }: { children: ReactNode }) {
  const { data: config, isLoading, error } = useSystemGetPublicConfig({
    query: {
      staleTime: Infinity, // Cache forever (configs don't change often)
      gcTime: Infinity, // Never garbage collect
      retry: 2,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
    },
  });

  const hasSetLocaleRef = useRef(false);

  // Apply detected locale from backend if user hasn't set a preference
  useEffect(() => {
    if (config?.detectedLocale && !hasSetLocaleRef.current) {
      // Check if user has an explicit language preference saved
      const savedLang = localStorage.getItem('i18nextLng');
      
      // Only apply backend's detected locale if:
      // 1. User hasn't saved a preference, OR
      // 2. Saved preference doesn't match any supported locale
      if (!savedLang || (savedLang !== 'es' && savedLang !== 'en')) {
        console.log('[i18n] Applying backend-detected locale:', config.detectedLocale);
        i18n.changeLanguage(config.detectedLocale);
        hasSetLocaleRef.current = true;
      }
    }
  }, [config?.detectedLocale]);

  const value: PublicConfigContextValue = {
    config: config || null,
    isLoading,
    error: error as Error | null,
  };

  return <PublicConfigContext.Provider value={value}>{children}</PublicConfigContext.Provider>;
}

export function usePublicConfigContext(): PublicConfigContextValue {
  const context = useContext(PublicConfigContext);
  if (context === undefined) {
    throw new Error('usePublicConfigContext must be used within a PublicConfigProvider');
  }
  return context;
}

